import { useEffect, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Layout from "../../Layout";
import AppHeader from "../AppHeader";
import { useCustomColors } from "../../hooks/useCustomColors";
import BannerSlider from "../lotto/BannerSlider";
import AuthGameHallPreview from "./AuthGameHallPreview";
import AuthPanel, { AuthMode } from "./AuthPanel";
import AuthDialog from "./AuthDialog";
import AuthNavDrawer from "./AuthNavDrawer";
import { PAGE_GUTTER, PAGE_MAX_WIDTH, SECTION_GAP } from "../../utils/spacing";

interface AuthMarketingLayoutProps {
  mode: AuthMode;
}

const ROUTE_FOR: Record<AuthMode, string> = {
  login: "/login",
  signup: "/sign-up",
};

const AuthMarketingLayout = ({ mode }: AuthMarketingLayoutProps) => {
  const colors = useCustomColors();
  const theme = useTheme();
  const navigate = useNavigate();
  // Below md (900px) the form becomes a dialog over the same marketing page.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [dialogOpen, setDialogOpen] = useState(true);

  // A deep link straight to any auth route opens the matching dialog.
  useEffect(() => {
    setDialogOpen(true);
  }, [mode]);

  const focusForm = () => {
    if (isMobile) {
      setDialogOpen(true);
      return;
    }
    document
      .getElementById("auth-form-panel")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Switching keeps the overlay open and syncs the URL, no reload.
  const switchMode = (next: AuthMode) => navigate(ROUTE_FOR[next]);

  const openDialog = (next: AuthMode) => {
    setDialogOpen(true);
    if (next !== mode) navigate(ROUTE_FOR[next]);
  };

  return (
    <Layout>
      <Box sx={{ minHeight: "100vh", overflowX: "hidden", background: colors.background.gradient }}>
        <AppHeader>
          {isMobile && <AuthNavDrawer mode={mode} onOpen={openDialog} />}
        </AppHeader>
        <Box sx={{ width: "100%", maxWidth: PAGE_MAX_WIDTH, mx: "auto", px: PAGE_GUTTER, py: SECTION_GAP }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 2fr) minmax(360px, 0.95fr)",
              },
              gap: SECTION_GAP,
              alignItems: "start",
            }}
          >
            <Box sx={{ minWidth: 0, order: { xs: 2, md: 1 } }}>
              <BannerSlider />
              <Box sx={{ mt: SECTION_GAP }}>
                <AuthGameHallPreview onExplore={focusForm} />
              </Box>
            </Box>
            {!isMobile && (
              <Box
                id="auth-form-panel"
                sx={{
                  minWidth: 0,
                  display: "flex",
                  justifyContent: { md: "flex-end" },
                  order: { xs: 1, md: 2 },
                }}
              >
                <AuthPanel mode={mode} onSwitch={switchMode} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {isMobile && (
        <AuthDialog
          open={dialogOpen}
          mode={mode}
          onClose={() => setDialogOpen(false)}
          onSwitch={switchMode}
        />
      )}
    </Layout>
  );
};

export default AuthMarketingLayout;
